const fail = (code) => {
  const error = new RangeError(code);
  error.code = code;
  throw error;
};

const addSafe = (left, right) => {
  const total = left + right;
  if (
    !Number.isSafeInteger(left) ||
    !Number.isSafeInteger(right) ||
    !Number.isSafeInteger(total)
  ) {
    fail("extra_values_cents_out_of_range");
  }
  return total;
};

export const entryCents = (entry) => {
  if (Number.isSafeInteger(entry?.totalCents)) return entry.totalCents;
  const value = Number(entry?.value);
  const cents = Math.round(value * 100);
  if (
    !Number.isFinite(value) ||
    !Number.isSafeInteger(cents) ||
    cents / 100 !== value
  ) {
    fail("extra_values_cents_out_of_range");
  }
  return cents;
};

export const documentIndicator = (document) =>
  (document?.history ?? []).reduce(
    (indicator, entry) => {
      if (entry?.isValid !== true) return indicator;
      return {
        count: indicator.count + 1,
        balanceCents: addSafe(indicator.balanceCents, entryCents(entry)),
      };
    },
    { count: 0, balanceCents: 0 },
  );

export const deriveExtraValuesSummary = (documents) => {
  try {
    const indicators = new Map();
    const totals = (documents ?? []).reduce(
      (current, document) => {
        const indicator = documentIndicator(document);
        if (indicator.count > 0) {
          indicators.set(String(document.wbs_code_id), indicator);
        }
        return (document?.history ?? []).reduce((documentTotals, entry) => {
          if (entry?.isValid !== true) return documentTotals;
          const cents = entryCents(entry);
          return cents >= 0
            ? {
                ...documentTotals,
                expensesCents: addSafe(documentTotals.expensesCents, cents),
              }
            : {
                ...documentTotals,
                savingsCents: addSafe(documentTotals.savingsCents, cents),
              };
        }, current);
      },
      { expensesCents: 0, savingsCents: 0 },
    );
    const { expensesCents, savingsCents } = totals;
    return {
      expensesCents,
      savingsCents: Math.abs(savingsCents),
      netCents: addSafe(expensesCents, savingsCents),
      activeTargetCount: indicators.size,
      indicators,
      error: null,
    };
  } catch (error) {
    return {
      expensesCents: 0,
      savingsCents: 0,
      netCents: 0,
      activeTargetCount: 0,
      indicators: new Map(),
      error: {
        code: error.code ?? "extra_values_cents_out_of_range",
        message: "Não foi possível calcular os totais com segurança.",
      },
    };
  }
};

const keyOf = (document) =>
  String(document?._id ?? document?.wbs_code_id ?? "");

export const extraValuesDocumentsReducer = (state, action) => {
  if (action.type === "replace_all") {
    return new Map(
      (action.documents ?? []).map((document) => [keyOf(document), document]),
    );
  }
  if (action.type === "upsert") {
    const { document } = action;
    if (!document) return state;
    const next = new Map(state);
    const existing = [...next].find(
      ([, candidate]) =>
        candidate?._id === document?._id ||
        candidate?.wbs_code_id === document?.wbs_code_id,
    );
    if (existing) next.delete(existing[0]);
    next.set(keyOf(document), document);
    return next;
  }
  return state;
};

export const documentsFromState = (state) => [...state.values()];
